import { useState, useRef, useCallback } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { parseUrl } from 'hub-http/helpers/url';
import styled from 'styled-components';
import { Image } from 'foundations-components/transitional/components/Image';
import { Flex } from 'foundations-components/components/Flex';
import { useGetCertificateQuery } from '../../../../__generated__/chirp/com/hubspot/learning/center/credentials/rpc/MyTeamChirpService.hooks';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import ACBadRequestError from '../../../components/error/ACBadRequestError';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
// @ts-expect-error bender-url appears not typed
import certBackgroundShapeUrl from 'bender-url!../../../../images/certificate-blob.svg';
import Raven from 'raven-js';
import { useMediaQuery } from '../../../utils/Styling';
import { View } from 'foundations-components/transitional/components/View';
import { useLocation } from 'quartz-routing/react-router';
import { useACNavigate } from '../../../hooks/useACNavigate';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CertificateBackground = styled.div.withConfig({
  displayName: "ViewCertificateModal__CertificateBackground"
})(["background-image:url(", ");background-position:top;background-repeat:no-repeat;background-size:contain;z-index:-1;width:100%;"], certBackgroundShapeUrl);
const ViewCertificateModal = ({
  onCloseClick,
  onDownloadCertificateClick = () => {},
  showShareButton = false,
  onShareButtonClick,
  retakeExamLink,
  aboveCertificationContent,
  belowCertificateContent,
  showProfileCta = false,
  grantCode,
  credentialTitle,
  grantedCredentialId,
  certificateImageUrl
}) => {
  const {
    data,
    loading: certificateQueryLoading,
    error: certificateQueryError
  } = useGetCertificateQuery({
    skip: !!certificateImageUrl || !grantedCredentialId,
    variables: {
      grantedCredentialId: grantedCredentialId || 0
    }
  });
  const [certImageBlob, setCertImageBlob] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  const errorReportedRef = useRef(false);
  const {
    isMobile
  } = useMediaQuery();
  const navigate = useACNavigate();
  const location = useLocation();
  const handleImageLoad = event => {
    try {
      var _cnv$getContext, _cnv$toBlob;
      const cnv = document.createElement('canvas');
      cnv.height = event.currentTarget.naturalHeight;
      cnv.width = event.currentTarget.naturalWidth;
      (_cnv$getContext = cnv.getContext('2d')) === null || _cnv$getContext === void 0 || _cnv$getContext.drawImage(event.currentTarget, 0, 0);
      (_cnv$toBlob = cnv.toBlob) === null || _cnv$toBlob === void 0 || _cnv$toBlob.call(cnv, blob => {
        if (blob) {
          setCertImageBlob(blob);
        }
      });
    } catch (err) {
      setImageLoadError(true);
      Raven.captureException(new Error('ViewCertificateModal: Error creating blob'));
    }
  };
  const handleImageError = () => {
    setImageLoadError(true);
  };
  const renderLoading = () => /*#__PURE__*/_jsx(DialogBody, {
    children: /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      layout: "centered",
      minBlockSize: "400px",
      size: "md"
    })
  });
  const renderError = () => /*#__PURE__*/_jsx(DialogBody, {
    minBlockSize: "400px",
    children: /*#__PURE__*/_jsx(ACBadRequestError, {})
  });
  const handleShareButtonClick = useCallback(() => {
    if (!grantedCredentialId) return;
    // Replace history entry to prevent certificate modal from reopening on browser back
    navigate(`/award-share/achievement/${grantedCredentialId}`, {
      replace: true,
      state: {
        code: grantCode,
        title: credentialTitle,
        backFromAwardShare: location
      }
    });
    onShareButtonClick === null || onShareButtonClick === void 0 || onShareButtonClick();
  }, [navigate, location, grantedCredentialId, grantCode, credentialTitle, onShareButtonClick]);
  const handleProfileCtaClick = useCallback(() => {
    navigate('/profile');
  }, [navigate]);
  const renderCertificate = () => {
    const resolvedCertificateUrl = certificateImageUrl || (data === null || data === void 0 ? void 0 : data.certificateUrl) || '';
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(DialogBody, {
        minBlockSize: "400px",
        children: [aboveCertificationContent, /*#__PURE__*/_jsx(CertificateBackground, {
          children: /*#__PURE__*/_jsx(View, {
            boxShadow: isMobile ? '200' : undefined,
            marginBlockStart: isMobile ? '800' : undefined,
            minBlockSize: isMobile ? undefined : '500px',
            children: /*#__PURE__*/_jsx(Image, {
              alt: "Certificate",
              crossOrigin: !imageLoadError ? 'anonymous' : undefined,
              onError: handleImageError,
              onLoad: handleImageLoad,
              paddingBlock: isMobile ? undefined : '600',
              paddingInline: isMobile ? undefined : '1600',
              src: resolvedCertificateUrl
            })
          })
        }), belowCertificateContent]
      }), /*#__PURE__*/_jsx(DialogFooter, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: "wrap",
          gap: isMobile ? '100' : '250',
          inlineSize: "100%",
          justifyContent: "center",
          children: [retakeExamLink && /*#__PURE__*/_jsx(Button, {
            href: retakeExamLink,
            marginInlineStart: "0",
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "profilePage.AcademyProfile.accreditations.retakeExam"
            })
          }), showShareButton && grantedCredentialId && /*#__PURE__*/_jsx(Button, {
            "data-test-id": "share-certificate-cta",
            marginInlineStart: "0",
            onClick: handleShareButtonClick,
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "certificateModal.shareCertificate"
            })
          }), /*#__PURE__*/_jsxs(Button, {
            "data-test-id": "download-certificate-cta",
            download: parseUrl(resolvedCertificateUrl).path.replace(/^.*\//, ''),
            href: certImageBlob ? URL.createObjectURL(certImageBlob) : resolvedCertificateUrl,
            marginInlineStart: "0",
            onClick: onDownloadCertificateClick,
            target: !certImageBlob ? '_blank' : undefined,
            use: "tertiary-light",
            children: [/*#__PURE__*/_jsx(IconHolder, {
              icon: DownloadIcon,
              padding: "0",
              size: "400"
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "certificateModal.downloadCertificate"
            })]
          }), showProfileCta && /*#__PURE__*/_jsx(Button, {
            marginInlineStart: "0",
            onClick: handleProfileCtaClick,
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "certificateModal.viewAllAchievements"
            })
          })]
        })
      })]
    });
  };
  const getModalContent = () => {
    if (!certificateImageUrl && certificateQueryLoading) {
      return renderLoading();
    }
    if (!certificateImageUrl && certificateQueryError) {
      return renderError();
    }
    const resolvedCertificateUrl = certificateImageUrl || (data === null || data === void 0 ? void 0 : data.certificateUrl);
    if (!resolvedCertificateUrl) {
      if (!errorReportedRef.current) {
        errorReportedRef.current = true;
        Raven.captureException(new Error('ViewCertificateModal: No certificate URL found'));
      }
      return renderError();
    }
    if (imageLoadError) {
      return renderError();
    }
    return renderCertificate();
  };
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "view-certificate-modal",
    minInlineSize: "min(1000px, 80%)",
    use: "conversational",
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onCloseClick
      })
    }), getModalContent()]
  });
};
export default ViewCertificateModal;